import type {
  InterviewLegacyScore,
  InterviewScore,
  InterviewSessionStatus,
  InterviewVoiceMetrics,
} from './interview'

// Teal HQ-style redesign (2026-08-24) -- 10 statuses, replacing the earlier
// 5-status model (saved/applied/interviewing/offered/rejected) entirely.
// "applying" removed 2026-09-01 (field-and-status-simplification backlog).
export type TrackerEntryStatus =
  | 'bookmarked'
  | 'applied'
  | 'interviewing'
  | 'negotiating'
  | 'accepted'
  | 'withdrew'
  | 'not_selected'
  | 'no_response'
  | 'archived'

// Board columns, in fixed left-to-right order. The 4 closed statuses are
// deliberately excluded -- they live in the Archived filter/tab instead
// (spec: "otherwise the board is 10 columns wide and unusable").
export const TRACKER_ACTIVE_STATUSES: TrackerEntryStatus[] = [
  'bookmarked',
  'applied',
  'interviewing',
  'negotiating',
  'accepted',
]

export const TRACKER_CLOSED_STATUSES: TrackerEntryStatus[] = [
  'withdrew',
  'not_selected',
  'no_response',
  'archived',
]

// All 10, active first then closed -- used anywhere a member can move an
// entry to any status regardless of tier (PATCH has no transition
// restriction per spec).
export const TRACKER_STATUSES: TrackerEntryStatus[] = [
  ...TRACKER_ACTIVE_STATUSES,
  ...TRACKER_CLOSED_STATUSES,
]

export const TRACKER_STATUS_LABELS: Record<TrackerEntryStatus, string> = {
  bookmarked: 'Bookmarked',
  applied: 'Applied',
  interviewing: 'Interviewing',
  // Displayed as "Offering" (2026-09-01 field-and-status-simplification) --
  // label-only rename, the stored/status value stays 'negotiating'.
  negotiating: 'Offering',
  accepted: 'Accepted',
  withdrew: 'I Withdrew',
  not_selected: 'Not Selected',
  no_response: 'No Response',
  archived: 'Archived',
}

// Reuses the app's existing tone vocabulary (job-badges.tsx / interview-
// history.tsx's StatusPill: emerald = success, blue = info/in-motion,
// amber = needs action, red = destructive/closed, slate = neutral/not
// started) rather than inventing new status colors. Some tones repeat
// across statuses by design -- there are only 5 tones for 10 statuses.
export type TrackerStatusTone = 'emerald' | 'blue' | 'amber' | 'red' | 'slate'

export const TRACKER_STATUS_TONE: Record<TrackerEntryStatus, TrackerStatusTone> = {
  bookmarked: 'slate',
  applied: 'blue',
  interviewing: 'amber',
  negotiating: 'amber',
  accepted: 'emerald',
  withdrew: 'slate',
  not_selected: 'red',
  no_response: 'red',
  archived: 'slate',
}

export interface TrackerRecommendationResponse {
  id: number
  tracker_entry_id: number
  tip: string
  is_fallback: boolean
  is_done: boolean
  created_at: string
}

export interface TrackerEntryResponse {
  id: number
  user_id: number
  title: string
  company: string | null
  job_url: string | null
  location: string | null
  job_description: string | null
  status: TrackerEntryStatus
  external_job_listing_id: number | null
  internal_job_posting_id: number | null
  date_saved: string
  excitement: number | null
  created_at: string
  updated_at: string
  // Present whenever an interview has actually been linked to this entry --
  // eligibility to launch one now depends on job_description being
  // non-empty (NOT on external_job_listing_id -- manual entries are equally
  // eligible per the Teal HQ-style redesign).
  interview_session_id: number | null
  interview_status: InterviewSessionStatus | null
  interview_score: InterviewScore | InterviewLegacyScore | null
  interview_voice_metrics: InterviewVoiceMetrics | null
  latest_recommendation: TrackerRecommendationResponse | null
}

export interface TrackerEntryListResponse {
  items: TrackerEntryResponse[]
  total: number
}

export interface TrackerEntryCreateRequest {
  external_job_listing_id?: number
  internal_job_posting_id?: number
  title?: string
  company?: string
  job_url?: string
  location?: string
  job_description?: string
}

export interface TrackerEntryUpdateRequest {
  title?: string
  company?: string
  job_url?: string
  location?: string
  job_description?: string
  status?: TrackerEntryStatus
  excitement?: number
}

export interface TrackerRecommendationDoneRequest {
  is_done: true
}

// AI priority tip across every entry in one board column -- not persisted,
// see TrackerService.generate_column_priority_tip on the backend.
export interface TrackerColumnTipResponse {
  tip: string
  entry_count: number
  is_fallback: boolean
}
